import { useMemo } from 'react';
import { useAccountDrawer } from 'context/accountDrawer';
import { formatInSemicolonString } from 'utils/formatUtils';
import { getTextOfTaskWarning } from 'utils/businessLogic';
import { CronoTaskTodoWarning } from 'crono-fe-common/types/enums/cronoTaskTodoWarning';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import RedirectSpanLink from '../RedirectSpanLink';
import { getMissingTemplateVariables } from 'crono-fe-common/utils';
import { captureException } from 'utils/exceptionUtils';

interface GeneralWarningsLabelProps {
  task: TaskTodo;
  onRedirect?: () => void;
}

export const GeneralWarningsLabel = ({
  task,
  onRedirect,
}: GeneralWarningsLabelProps) => {
  const { openContactDrawer } = useAccountDrawer();
  const { prospectId, accountId } = task;

  const handleRedirect = (redirectAction: () => void) => {
    redirectAction();
    if (onRedirect) {
      onRedirect();
    }
  };

  const taskGeneralWarnings = useMemo(() => {
    const messages: string[] = [];

    if (
      task.completed ||
      !task.active ||
      !task.warnings ||
      !task.warnings.length
    )
      return { messages, showFixLink: false };

    const showFixLink = !(
      task.warnings.length === 1 &&
      task.warnings[0] === CronoTaskTodoWarning.TemplateVariableCanNotBeReplaced
    );

    task.warnings.forEach((warning) => {
      if (warning === CronoTaskTodoWarning.TemplateVariableCanNotBeReplaced) {
        if (task.template) {
          const missingVariables = getMissingTemplateVariables(
            task.template.content,
            task.template.subject,
          );
          if (missingVariables.length) {
            messages.push(getTextOfTaskWarning(warning));
          }
        }
      } else {
        messages.push(getTextOfTaskWarning(warning));
      }
    });

    return { messages, showFixLink };
  }, [task]);

  return taskGeneralWarnings.messages.length > 0 ? (
    <span>
      {formatInSemicolonString(taskGeneralWarnings.messages)}.
      {taskGeneralWarnings.showFixLink && (
        <>
          {' '}
          Fix it{' '}
          <RedirectSpanLink
            onClick={() =>
              handleRedirect(() => {
                if (accountId) {
                  openContactDrawer({ prospectId, accountId });
                } else {
                  const debugData = {
                    taskId: task?.id,
                    prospectId: task?.prospectId,
                    type: task?.type,
                  };
                  console.error(
                    'Cannot open contact side tab because accountId is missing on task',
                    debugData,
                  );
                  captureException(
                    new Error(
                      'Cannot open contact side tab because accountId is missing on task: ' +
                        JSON.stringify(debugData),
                    ),
                  );
                }
              })
            }
          />
        </>
      )}
    </span>
  ) : null;
};
